import type { Updater, PaginationState } from '@tanstack/react-table';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';

import { removeNullishFromObj } from '@shared/utils/helpers/remove-nullish-from-obj';
import type { TBaseQuery } from '@shared/types/base-query';

export function useSetPaginationInUrl<T extends Record<string, unknown>>(
  tableKey: string = ''
) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = qs.parse(search, {
    ignoreQueryPrefix: true,
  });

  // GET/SET filters from/in URL
  const tableFiltersInUrl = queryParams[tableKey] as T & TBaseQuery;
  const setTableFiltersInUrl = (filters: T & TBaseQuery) => {
    navigate({
      search: qs.stringify(
        {
          ...queryParams,
          [tableKey]: filters,
        },
        { skipNulls: true }
      ),
    });
  };

  return {
    tableFiltersInUrl: removeNullishFromObj<T & TBaseQuery>(tableFiltersInUrl),
    setTableFiltersInUrl,
  };
}

const usePageSizeLogic = (tableKey: string) => {
  const { tableFiltersInUrl } = useSetPaginationInUrl(tableKey);

  const pageSizeStorageKey = `${tableKey}_pageSize`;

  return {
    getCurrentPageSize: () => {
      const storedPageSize = localStorage.getItem(pageSizeStorageKey) || 0;
      return (
        Number(storedPageSize) ||
        tableFiltersInUrl?.page_size ||
        DEFAULT_PER_PAGE
      );
    },

    setPageSizeInLocalStorage: (pageSize?: number) => {
      if (!pageSize) return;
      localStorage.setItem(pageSizeStorageKey, pageSize.toString());
    },

    generatePageSizeToSet: (pageSize?: number) => {
      const isPageSizeDefault = pageSize === DEFAULT_PER_PAGE;
      return isPageSizeDefault ? undefined : pageSize;
    },
  };
};

const usePageNumberLogic = (tableKey: string) => {
  const { tableFiltersInUrl } = useSetPaginationInUrl(tableKey);

  return {
    getCurrentPageNumber: () => tableFiltersInUrl?.page_number || DEFAULT_PAGE,
    generatePageNumberToSet: (pageNumber?: number) => {
      const isPageNumberDefault = pageNumber === DEFAULT_PAGE;
      return isPageNumberDefault ? undefined : pageNumber;
    },
  };
};

export const usePagination = (tableKey: string) => {
  const { tableFiltersInUrl, setTableFiltersInUrl } =
    useSetPaginationInUrl(tableKey);

  const {
    getCurrentPageSize,
    generatePageSizeToSet,
    setPageSizeInLocalStorage,
  } = usePageSizeLogic(tableKey);
  const { getCurrentPageNumber, generatePageNumberToSet } =
    usePageNumberLogic(tableKey);

  const pagination = {
    pageIndex: getCurrentPageNumber() - 1,
    pageSize: getCurrentPageSize(),
  };

  const setPagination = (pagination: { page?: number; perPage?: number }) => {
    setTableFiltersInUrl({
      ...tableFiltersInUrl,
      page_number: generatePageNumberToSet(pagination.page),
      page_size: generatePageSizeToSet(pagination.perPage),
    });
    setPageSizeInLocalStorage(pagination.perPage);
  };

  const onPaginationChange = (updaterOrValue: Updater<PaginationState>) => {
    const newPagination =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(pagination)
        : updaterOrValue;
    setPagination({
      page: newPagination.pageIndex + 1,
      perPage: newPagination.pageSize,
    });
  };

  return { pagination, onPaginationChange };
};
